import { DialogProvider } from 'accelerator-dialog/lib/DialogProvider';
import { AppRouterProvider } from 'accelerator-router';
import { PlacesFluentProvider } from 'hybridspace-common/lib/PlacesFluentProvider';
import { ErrorBoundaryLogged, logAppBoot } from 'hybridspace-telemetry';
import { UnifiedTelemetryActionDomain } from 'owa-analytics-generic-action';
/* eslint-disable-next-line owa-custom-rules/forbid-svg-import -- (https://aka.ms/OWALintWiki)
 * BASELINE: DO NOT COPY/PASTE! UNVERIFIED-SVG-DISABLE: confirm no @fluentui/react-icons component exists for this asset and a direct SVG import is truly required. If confirmed, replace this comment with "Direct SVG import required; component-based icon (@fluentui/react-icons) not available for this asset". If an icon replacement is possible, migrate to it and remove the import and this disable
 *	> Referencing SVG files that are available as Fluent v9 icons is forbidden. Use the corresponding Fluent v9 icon instead. Browse available icons in the Fluent UI documentation: https://storybooks.fluentui.dev/react/?path=/docs/fluent-system-icons_icons-catalog--docs */
import microsoftPlacesLogo from 'owa-appbar-icons/lib/microsoftPlaces.svg';
import loc from 'owa-localize';
import { appMicrosoftPlaces as strings_appMicrosoftPlaces } from 'owa-locstrings/lib/strings/appMicrosoftPlaces.locstring.json';
import { observer } from 'owa-mobx-react';
import { NotificationBarHost } from 'owa-notification-bar';
import { getQueryStringParameter, hasQueryStringParameter } from 'owa-querystring';
import { useBodyMinWidth } from 'owa-react-hooks/lib/useBodyMinWidth';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { useDocumentIcon } from 'owa-react-hooks/lib/useDocumentIcon';
import { useDocumentTitle } from 'owa-react-hooks/lib/useDocumentTitle';
import { type UTActionDomain } from '@outlook/unified_telemetry';
import usePlacesGlobalHotkey from 'places-hotkeys/lib/usePlacesGlobalHotkey';
import { isAppInCiriticalState } from 'places-user-session-stores';
import React from 'react';
import { getLoadedPageToLog } from '../utils/getLoadedPageToLog';
import { HybridspaceLoadingScreen } from './HybridspaceLoadingScreen';
import { mainModuleStyle } from './HybridspaceMainModule.scss';
import PlacesPermissionValidation from './PlacesPermissionValidation';
import { createPlacesRoutes } from './PlacesRouter/createPlacesRoutes';

import type { ErrorBoundaryLoggedData } from 'hybridspace-telemetry';

const mainModule: React.CSSProperties = {
    width: '100%',
    height: '100%',
    position: 'relative',
    display: 'flex',
    flexFlow: 'column nowrap',
};
// Fallback element so user does not see a blank screen while router does its work
const fallbackElement = <HybridspaceLoadingScreen />;

const defaultExport: React.FunctionComponent = observer(function HybridspaceMainModule() {
    useBodyMinWidth('unset');
    // During the change-organizer MSAL auth round-trip, /places/ is just a transient hop.
    // Skip overriding the title so it stays as the change-organizer module name set during boot.
    const isChangeOrganizerHop = document.documentElement.dataset.changeOrganizer === '1';
    useDocumentTitle(isChangeOrganizerHop ? undefined : loc(strings_appMicrosoftPlaces));
    useDocumentIcon(microsoftPlacesLogo, 'image/svg+xml');

    // Request from MetaOs will have query strings: &ep=metaOS and &hostApp=xxxx (Teams, Monarch, etc)
    const hostApp = getQueryStringParameter('hostApp');
    usePlacesGlobalHotkey();

    const routes = useComputedValue(createPlacesRoutes, []);

    const logAppBootCallback = React.useCallback(
        (data: ErrorBoundaryLoggedData) => {
            const loadedPage = getLoadedPageToLog(routes);
            logAppBoot(loadedPage, data.ActionSuccess, data.ActionFailureInfo);
        },
        [routes]
    );

    return (
        <ErrorBoundaryLogged
            parentName={'MainModule'}
            runLogCallback={logAppBootCallback}
            // we want to reset and throw after catch to let OWA handle the exception
            resetAndThrowAfterCatch={true}
        >
            <UnifiedTelemetryActionDomain name={'Places'}>
                <PlacesFluentProvider style={mainModule} className={mainModuleStyle}>
                    <PlacesPermissionValidation hostApp={hostApp}>
                        {/* TODO: review 2nd loading screen */}
                        {!hasQueryStringParameter('devRmvMmLoadingScreen') &&
                            isAppInCiriticalState() && <HybridspaceLoadingScreen />}
                        <AppRouterProvider
                            basename="/places"
                            routes={routes}
                            fallbackElement={fallbackElement}
                        />
                        <NotificationBarHost hostId={'HybridspaceNotificationBarHost'} />
                        <NotificationBarHost hostId={'AddisonNotificationBarHost'} />
                    </PlacesPermissionValidation>
                    <DialogProvider />
                </PlacesFluentProvider>
            </UnifiedTelemetryActionDomain>
        </ErrorBoundaryLogged>
    );
}, 'HybridspaceMainModule');
export default defaultExport;
